import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import oauthService from '../services/oauthService';

const AVAILABLE_SCOPES = [
  { id: 'openid', label: 'openid', desc: 'Verify user identity (OIDC standard)' },
  { id: 'profile', label: 'profile', desc: 'Display name, profile picture' },
  { id: 'email', label: 'email', desc: 'Verified email address' },
  { id: 'phone', label: 'phone', desc: 'Mobile phone number' },
  { id: 'offline_access', label: 'offline_access', desc: 'Refresh token for background access' },
  { id: 'marketer.info', label: 'marketer.info', desc: 'Marketer referrer code, rank, details' },
];

const OAuthManagementPage = () => {
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' | 'apikeys'
  const [clients, setClients] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modals state
  const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [isCreateKeyOpen, setIsCreateKeyOpen] = useState(false);
  const [secretRevealModal, setSecretRevealModal] = useState(null); // { title, clientId, secret, isApiKey }
  const [selectedClient, setSelectedClient] = useState(null);

  // Form states for Create/Edit Client
  const [clientForm, setClientForm] = useState({
    clientName: '',
    clientType: 'confidential',
    redirectUris: [''],
    allowedScopes: ['openid', 'profile', 'email', 'phone', 'offline_access'],
    logoUrl: '',
    description: '',
    privacyUrl: '',
    isActive: true,
  });

  // Form state for Create API Key
  const [keyForm, setKeyForm] = useState({
    name: '',
    scopes: ['marketer:read'],
    expiresInDays: 90,
  });

  const [copiedKey, setCopiedKey] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await oauthService.getClients({ limit: 50, offset: 0 });
      if (res && res.data && res.data.clients) {
        setClients(res.data.clients);
      } else if (res && res.clients) {
        setClients(res.clients);
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setError('Failed to load OAuth clients.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await oauthService.getApiKeys({ limit: 50, offset: 0 });
      if (res && res.data && res.data.apiKeys) {
        setApiKeys(res.data.apiKeys);
      } else if (res && res.apiKeys) {
        setApiKeys(res.apiKeys);
      } else {
        setApiKeys([]);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    } else {
      fetchApiKeys();
    }
  }, [activeTab, fetchClients, fetchApiKeys]);

  const handleCopy = (text, keyName) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedKey(keyName);
      setTimeout(() => setCopiedKey(''), 2500);
    }
  };

  // Create Client Submit
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const cleanedUris = clientForm.redirectUris.map((u) => u.trim()).filter(Boolean);
      if (cleanedUris.length === 0) {
        alert('Please specify at least one redirect URI.');
        setLoading(false);
        return;
      }

      const res = await oauthService.createClient({
        clientName: clientForm.clientName,
        clientType: clientForm.clientType,
        redirectUris: cleanedUris,
        allowedScopes: clientForm.allowedScopes,
        logoUrl: clientForm.logoUrl || null,
        description: clientForm.description || null,
        privacyUrl: clientForm.privacyUrl || null,
      });

      const clientData = res.data || res;
      setIsCreateClientOpen(false);

      if (clientData.clientSecret) {
        setSecretRevealModal({
          title: 'Client Credentials Generated',
          clientId: clientData.clientId,
          secret: clientData.clientSecret,
          isApiKey: false,
        });
      }
      fetchClients();
    } catch (err) {
      console.error('Create client failed:', err);
      alert(err.message || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Client Modal
  const openEditModal = (client) => {
    setSelectedClient(client);
    setClientForm({
      clientName: client.clientName,
      clientType: client.clientType,
      redirectUris: client.redirectUris?.length ? [...client.redirectUris] : [''],
      allowedScopes: client.allowedScopes || [],
      logoUrl: client.logoUrl || '',
      description: client.description || '',
      privacyUrl: client.privacyUrl || '',
      isActive: client.isActive,
    });
    setIsEditClientOpen(true);
  };

  // Update Client Submit
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    try {
      setLoading(true);
      const cleanedUris = clientForm.redirectUris.map((u) => u.trim()).filter(Boolean);
      await oauthService.updateClient(selectedClient.id, {
        clientName: clientForm.clientName,
        clientType: clientForm.clientType,
        redirectUris: cleanedUris,
        allowedScopes: clientForm.allowedScopes,
        logoUrl: clientForm.logoUrl || null,
        description: clientForm.description || null,
        privacyUrl: clientForm.privacyUrl || null,
        isActive: clientForm.isActive,
      });
      setIsEditClientOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      console.error('Update client failed:', err);
      alert(err.message || 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  // Rotate Secret
  const handleRotateSecret = async (client) => {
    if (!window.confirm(`Rotate secret for "${client.clientName}"? Any application using the current secret will be immediately disrupted.`)) {
      return;
    }
    try {
      setLoading(true);
      const res = await oauthService.rotateClientSecret(client.id);
      const data = res.data || res;
      setSecretRevealModal({
        title: 'New Client Secret Generated',
        clientId: data.clientId,
        secret: data.clientSecret,
        isApiKey: false,
      });
      fetchClients();
    } catch (err) {
      console.error('Failed to rotate secret:', err);
      alert(err.message || 'Failed to rotate secret');
    } finally {
      setLoading(false);
    }
  };

  // Delete Client
  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.clientName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      await oauthService.deleteClient(client.id);
      fetchClients();
    } catch (err) {
      console.error('Failed to delete client:', err);
      alert(err.message || 'Failed to delete client');
    } finally {
      setLoading(false);
    }
  };

  // Create API Key Submit
  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const expiresAt = keyForm.expiresInDays > 0
        ? new Date(Date.now() + keyForm.expiresInDays * 24 * 60 * 60 * 1000).toISOString()
        : null;

      const res = await oauthService.createApiKey({
        name: keyForm.name,
        scopes: keyForm.scopes,
        expiresAt,
      });

      const keyData = res.data || res;
      setIsCreateKeyOpen(false);
      setSecretRevealModal({
        title: 'API Key Created',
        clientId: keyData.name,
        secret: keyData.rawKey,
        isApiKey: true,
      });
      fetchApiKeys();
    } catch (err) {
      console.error('Create API Key failed:', err);
      alert(err.message || 'Failed to create API Key');
    } finally {
      setLoading(false);
    }
  };

  // Revoke API Key
  const handleRevokeApiKey = async (key) => {
    if (!window.confirm(`Revoke API key "${key.name}"? Applications using this key will immediately lose access.`)) {
      return;
    }
    try {
      setLoading(true);
      await oauthService.revokeApiKey(key.id);
      fetchApiKeys();
    } catch (err) {
      console.error('Failed to revoke API key:', err);
      alert(err.message || 'Failed to revoke API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='p-6 max-w-7xl mx-auto'>
      <PageHeader
        title='SSO Provider & API Keys'
        subtitle='Manage OAuth 2.0 / OpenID Connect client applications and developer API keys'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Master' },
          { name: 'SSO & API Keys' },
        ]}
      />

      {/* Tabs */}
      <div className='flex items-center justify-between border-b border-gray-200 mb-6'>
        <div className='flex space-x-8'>
          <button
            onClick={() => setActiveTab('clients')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'clients'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
            </svg>
            OAuth 2.0 Clients (SSO)
            <span className='ml-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800'>
              {clients.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('apikeys')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'apikeys'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' />
            </svg>
            Server API Keys (M2M)
            <span className='ml-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800'>
              {apiKeys.length}
            </span>
          </button>
        </div>

        <div>
          {activeTab === 'clients' ? (
            <button
              onClick={() => {
                setClientForm({
                  clientName: '',
                  clientType: 'confidential',
                  redirectUris: [''],
                  allowedScopes: ['openid', 'profile', 'email', 'phone', 'offline_access'],
                  logoUrl: '',
                  description: '',
                  privacyUrl: '',
                  isActive: true,
                });
                setIsCreateClientOpen(true);
              }}
              className='inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
              Register Application
            </button>
          ) : (
            <button
              onClick={() => {
                setKeyForm({
                  name: '',
                  scopes: ['marketer:read'],
                  expiresInDays: 90,
                });
                setIsCreateKeyOpen(true);
              }}
              className='inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
              Generate API Key
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className='p-4 mb-6 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm'>
          {error}
        </div>
      )}

      {/* Tab 1: Clients */}
      {activeTab === 'clients' && (
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200 text-left text-sm'>
              <thead className='bg-gray-50 text-gray-500 font-medium'>
                <tr>
                  <th className='py-3.5 px-6'>Application</th>
                  <th className='py-3.5 px-6'>Client ID</th>
                  <th className='py-3.5 px-6'>Type</th>
                  <th className='py-3.5 px-6'>Redirect URIs</th>
                  <th className='py-3.5 px-6'>Status</th>
                  <th className='py-3.5 px-6 text-right'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 text-gray-700'>
                {loading && clients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='py-8 text-center text-gray-400'>
                      Loading OAuth clients...
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='py-12 text-center'>
                      <div className='flex flex-col items-center justify-center text-gray-400'>
                        <svg className='w-12 h-12 mb-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                        </svg>
                        <p className='font-medium text-gray-600'>No OAuth applications registered yet.</p>
                        <p className='text-sm mt-1'>Click "Register Application" to enable SSO for a mobile or web app.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.id} className='hover:bg-gray-50/80 transition-colors'>
                      <td className='py-4 px-6'>
                        <div className='flex items-center gap-3'>
                          {client.logoUrl ? (
                            <img src={client.logoUrl} alt='' className='w-10 h-10 rounded-lg object-contain bg-gray-100 p-1 border border-gray-200' />
                          ) : (
                            <div className='w-10 h-10 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center border border-blue-200'>
                              {client.clientName.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className='font-semibold text-gray-900'>{client.clientName}</div>
                            {client.description && (
                              <div className='text-xs text-gray-500 max-w-xs truncate'>{client.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-6'>
                        <div className='flex items-center gap-2 font-mono text-xs text-gray-600 bg-gray-100 py-1 px-2.5 rounded-md border border-gray-200 w-fit'>
                          <span>{client.clientId}</span>
                          <button
                            onClick={() => handleCopy(client.clientId, `client-${client.id}`)}
                            className='hover:text-blue-600'
                            title='Copy Client ID'
                          >
                            {copiedKey === `client-${client.id}` ? (
                              <span className='text-green-600 font-sans text-xs'>Copied!</span>
                            ) : (
                              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className='py-4 px-6'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                            client.clientType === 'public'
                              ? 'bg-purple-100 text-purple-800 border border-purple-200'
                              : 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                          }`}
                        >
                          {client.clientType === 'public' ? 'Public (PKCE)' : 'Confidential'}
                        </span>
                      </td>
                      <td className='py-4 px-6'>
                        <div className='flex flex-col gap-1 max-w-xs'>
                          {client.redirectUris?.map((uri, idx) => (
                            <span key={idx} className='text-xs font-mono bg-gray-50 text-gray-600 px-2 py-0.5 rounded border border-gray-200 truncate' title={uri}>
                              {uri}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className='py-4 px-6'>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            client.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${client.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          {client.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className='py-4 px-6 text-right space-x-2'>
                        <button
                          onClick={() => openEditModal(client)}
                          className='text-gray-600 hover:text-blue-600 text-sm font-medium'
                        >
                          Edit
                        </button>
                        {client.clientType === 'confidential' && (
                          <button
                            onClick={() => handleRotateSecret(client)}
                            className='text-amber-600 hover:text-amber-700 text-sm font-medium'
                          >
                            Rotate Secret
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClient(client)}
                          className='text-red-600 hover:text-red-700 text-sm font-medium'
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: API Keys */}
      {activeTab === 'apikeys' && (
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200 text-left text-sm'>
              <thead className='bg-gray-50 text-gray-500 font-medium'>
                <tr>
                  <th className='py-3.5 px-6'>Key Name</th>
                  <th className='py-3.5 px-6'>Key Prefix</th>
                  <th className='py-3.5 px-6'>Scopes</th>
                  <th className='py-3.5 px-6'>Created</th>
                  <th className='py-3.5 px-6'>Expires</th>
                  <th className='py-3.5 px-6'>Status</th>
                  <th className='py-3.5 px-6 text-right'>Action</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 text-gray-700'>
                {loading && apiKeys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='py-8 text-center text-gray-400'>
                      Loading API keys...
                    </td>
                  </tr>
                ) : apiKeys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='py-12 text-center'>
                      <div className='flex flex-col items-center justify-center text-gray-400'>
                        <svg className='w-12 h-12 mb-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' />
                        </svg>
                        <p className='font-medium text-gray-600'>No API keys created yet.</p>
                        <p className='text-sm mt-1'>Generate an API key for machine-to-machine developer integrations.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  apiKeys.map((k) => (
                    <tr key={k.id} className='hover:bg-gray-50/80 transition-colors'>
                      <td className='py-4 px-6 font-medium text-gray-900'>{k.name}</td>
                      <td className='py-4 px-6 font-mono text-xs text-gray-600 bg-gray-50 px-2 rounded'>
                        {k.keyPrefix}_********************
                      </td>
                      <td className='py-4 px-6'>
                        <div className='flex gap-1 flex-wrap'>
                          {k.scopes?.map((s, idx) => (
                            <span key={idx} className='bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-xs'>
                              {s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className='py-4 px-6 text-xs text-gray-500'>
                        {new Date(k.createdAt).toLocaleDateString()}
                      </td>
                      <td className='py-4 px-6 text-xs text-gray-500'>
                        {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className='py-4 px-6'>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            k.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${k.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          {k.isActive ? 'Active' : 'Revoked'}
                        </span>
                      </td>
                      <td className='py-4 px-6 text-right'>
                        {k.isActive && (
                          <button
                            onClick={() => handleRevokeApiKey(k)}
                            className='text-red-600 hover:text-red-700 text-sm font-medium'
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Create Client */}
      {isCreateClientOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between pb-4 border-b border-gray-100'>
              <h3 className='text-lg font-bold text-gray-900'>Register OAuth Application</h3>
              <button onClick={() => setIsCreateClientOpen(false)} className='text-gray-400 hover:text-gray-600'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateClient} className='space-y-4 mt-4'>
              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Application Name *</label>
                <input
                  type='text'
                  required
                  placeholder='e.g. Acme Marketer Portal'
                  value={clientForm.clientName}
                  onChange={(e) => setClientForm({ ...clientForm, clientName: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Client Type *</label>
                <div className='grid grid-cols-2 gap-3'>
                  <label
                    className={`border rounded-lg p-3 cursor-pointer text-sm flex flex-col gap-1 ${
                      clientForm.clientType === 'confidential' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200'
                    }`}
                  >
                    <div className='flex items-center gap-2 font-medium text-gray-900'>
                      <input
                        type='radio'
                        name='clientType'
                        value='confidential'
                        checked={clientForm.clientType === 'confidential'}
                        onChange={(e) => setClientForm({ ...clientForm, clientType: e.target.value })}
                        className='text-blue-600'
                      />
                      Confidential
                    </div>
                    <span className='text-xs text-gray-500'>Backend web servers holding secret safely</span>
                  </label>
                  <label
                    className={`border rounded-lg p-3 cursor-pointer text-sm flex flex-col gap-1 ${
                      clientForm.clientType === 'public' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200'
                    }`}
                  >
                    <div className='flex items-center gap-2 font-medium text-gray-900'>
                      <input
                        type='radio'
                        name='clientType'
                        value='public'
                        checked={clientForm.clientType === 'public'}
                        onChange={(e) => setClientForm({ ...clientForm, clientType: e.target.value })}
                        className='text-blue-600'
                      />
                      Public (PKCE)
                    </div>
                    <span className='text-xs text-gray-500'>Flutter mobile apps or SPAs without server secret</span>
                  </label>
                </div>
              </div>

              <div>
                <div className='flex items-center justify-between mb-1'>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider'>Redirect URIs *</label>
                  <button
                    type='button'
                    onClick={() => setClientForm({ ...clientForm, redirectUris: [...clientForm.redirectUris, ''] })}
                    className='text-xs text-blue-600 hover:text-blue-700 font-medium'
                  >
                    + Add URI
                  </button>
                </div>
                {clientForm.redirectUris.map((uri, idx) => (
                  <div key={idx} className='flex items-center gap-2 mb-2'>
                    <input
                      type='text'
                      required
                      placeholder='https://myapp.com/oauth/callback or myapp://oauth'
                      value={uri}
                      onChange={(e) => {
                        const newUris = [...clientForm.redirectUris];
                        newUris[idx] = e.target.value;
                        setClientForm({ ...clientForm, redirectUris: newUris });
                      }}
                      className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none'
                    />
                    {clientForm.redirectUris.length > 1 && (
                      <button
                        type='button'
                        onClick={() => {
                          const newUris = clientForm.redirectUris.filter((_, i) => i !== idx);
                          setClientForm({ ...clientForm, redirectUris: newUris });
                        }}
                        className='text-gray-400 hover:text-red-500 p-1'
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Allowed Scopes</label>
                <div className='space-y-2 border border-gray-200 rounded-lg p-3 max-h-36 overflow-y-auto bg-gray-50/50'>
                  {AVAILABLE_SCOPES.map((sc) => (
                    <label key={sc.id} className='flex items-start gap-2 text-xs cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={clientForm.allowedScopes.includes(sc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setClientForm({ ...clientForm, allowedScopes: [...clientForm.allowedScopes, sc.id] });
                          } else {
                            setClientForm({
                              ...clientForm,
                              allowedScopes: clientForm.allowedScopes.filter((s) => s !== sc.id),
                            });
                          }
                        }}
                        className='rounded text-blue-600 mt-0.5'
                      />
                      <div>
                        <span className='font-mono font-semibold text-gray-800'>{sc.label}</span>
                        <p className='text-gray-500'>{sc.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Logo URL (Optional)</label>
                <input
                  type='url'
                  placeholder='https://myapp.com/logo.png'
                  value={clientForm.logoUrl}
                  onChange={(e) => setClientForm({ ...clientForm, logoUrl: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder='Describe the client application purpose...'
                  value={clientForm.description}
                  onChange={(e) => setClientForm({ ...clientForm, description: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Privacy Policy URL (Optional)</label>
                <input
                  type='url'
                  placeholder='https://myapp.com/privacy'
                  value={clientForm.privacyUrl}
                  onChange={(e) => setClientForm({ ...clientForm, privacyUrl: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
                <p className='text-xs text-gray-400 mt-1'>Shown as the app's privacy notice link on the consent screen.</p>
              </div>

              <div className='flex justify-end gap-3 pt-4 border-t border-gray-100'>
                <button
                  type='button'
                  onClick={() => setIsCreateClientOpen(false)}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50'
                >
                  {loading ? 'Creating...' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Client */}
      {isEditClientOpen && selectedClient && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto'>
            <div className='flex items-center justify-between pb-4 border-b border-gray-100'>
              <h3 className='text-lg font-bold text-gray-900'>Edit Application</h3>
              <button onClick={() => setIsEditClientOpen(false)} className='text-gray-400 hover:text-gray-600'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdateClient} className='space-y-4 mt-4'>
              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Application Name *</label>
                <input
                  type='text'
                  required
                  value={clientForm.clientName}
                  onChange={(e) => setClientForm({ ...clientForm, clientName: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Status</label>
                <label className='flex items-center gap-2 text-sm cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={clientForm.isActive}
                    onChange={(e) => setClientForm({ ...clientForm, isActive: e.target.checked })}
                    className='rounded text-blue-600'
                  />
                  <span className='font-medium text-gray-800'>Active (Application can authenticate users)</span>
                </label>
              </div>

              <div>
                <div className='flex items-center justify-between mb-1'>
                  <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider'>Redirect URIs *</label>
                  <button
                    type='button'
                    onClick={() => setClientForm({ ...clientForm, redirectUris: [...clientForm.redirectUris, ''] })}
                    className='text-xs text-blue-600 hover:text-blue-700 font-medium'
                  >
                    + Add URI
                  </button>
                </div>
                {clientForm.redirectUris.map((uri, idx) => (
                  <div key={idx} className='flex items-center gap-2 mb-2'>
                    <input
                      type='text'
                      required
                      value={uri}
                      onChange={(e) => {
                        const newUris = [...clientForm.redirectUris];
                        newUris[idx] = e.target.value;
                        setClientForm({ ...clientForm, redirectUris: newUris });
                      }}
                      className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none'
                    />
                    {clientForm.redirectUris.length > 1 && (
                      <button
                        type='button'
                        onClick={() => {
                          const newUris = clientForm.redirectUris.filter((_, i) => i !== idx);
                          setClientForm({ ...clientForm, redirectUris: newUris });
                        }}
                        className='text-gray-400 hover:text-red-500 p-1'
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Allowed Scopes</label>
                <div className='space-y-2 border border-gray-200 rounded-lg p-3 max-h-36 overflow-y-auto bg-gray-50/50'>
                  {AVAILABLE_SCOPES.map((sc) => (
                    <label key={sc.id} className='flex items-start gap-2 text-xs cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={clientForm.allowedScopes.includes(sc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setClientForm({ ...clientForm, allowedScopes: [...clientForm.allowedScopes, sc.id] });
                          } else {
                            setClientForm({
                              ...clientForm,
                              allowedScopes: clientForm.allowedScopes.filter((s) => s !== sc.id),
                            });
                          }
                        }}
                        className='rounded text-blue-600 mt-0.5'
                      />
                      <div>
                        <span className='font-mono font-semibold text-gray-800'>{sc.label}</span>
                        <p className='text-gray-500'>{sc.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Logo URL</label>
                <input
                  type='url'
                  value={clientForm.logoUrl}
                  onChange={(e) => setClientForm({ ...clientForm, logoUrl: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Description</label>
                <textarea
                  rows={2}
                  value={clientForm.description}
                  onChange={(e) => setClientForm({ ...clientForm, description: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Privacy Policy URL</label>
                <input
                  type='url'
                  placeholder='https://myapp.com/privacy'
                  value={clientForm.privacyUrl}
                  onChange={(e) => setClientForm({ ...clientForm, privacyUrl: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
                <p className='text-xs text-gray-400 mt-1'>Shown as the app's privacy notice link on the consent screen.</p>
              </div>

              <div className='flex justify-end gap-3 pt-4 border-t border-gray-100'>
                <button
                  type='button'
                  onClick={() => setIsEditClientOpen(false)}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50'
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Create API Key */}
      {isCreateKeyOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='bg-white rounded-2xl max-w-md w-full p-6 shadow-xl'>
            <div className='flex items-center justify-between pb-4 border-b border-gray-100'>
              <h3 className='text-lg font-bold text-gray-900'>Generate Server API Key</h3>
              <button onClick={() => setIsCreateKeyOpen(false)} className='text-gray-400 hover:text-gray-600'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateApiKey} className='space-y-4 mt-4'>
              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Key Name *</label>
                <input
                  type='text'
                  required
                  placeholder='e.g. Analytics Backend Service'
                  value={keyForm.name}
                  onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                />
              </div>

              <div>
                <label className='block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1'>Expiration</label>
                <select
                  value={keyForm.expiresInDays}
                  onChange={(e) => setKeyForm({ ...keyForm, expiresInDays: parseInt(e.target.value, 10) })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none'
                >
                  <option value={30}>30 Days</option>
                  <option value={90}>90 Days</option>
                  <option value={180}>180 Days</option>
                  <option value={365}>1 Year</option>
                  <option value={0}>Never Expire</option>
                </select>
              </div>

              <div className='flex justify-end gap-3 pt-4 border-t border-gray-100'>
                <button
                  type='button'
                  onClick={() => setIsCreateKeyOpen(false)}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50'
                >
                  {loading ? 'Generating...' : 'Generate Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Secret / Key Created Modal (One-Time Display) */}
      {secretRevealModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm'>
          <div className='bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100'>
            <div className='flex items-center gap-3 text-green-600 mb-3'>
              <div className='w-10 h-10 rounded-full bg-green-100 flex items-center justify-center'>
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <h3 className='text-xl font-bold text-gray-900'>{secretRevealModal.title}</h3>
            </div>

            <div className='p-3.5 mb-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex gap-2'>
              <svg className='w-5 h-5 flex-shrink-0 text-amber-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
              </svg>
              <span>
                <strong>Important:</strong> Copy this secret now. For security reasons, it is stored as a one-way cryptographic hash and will <strong>never be shown again</strong>.
              </span>
            </div>

            <div className='space-y-3'>
              {!secretRevealModal.isApiKey && (
                <div>
                  <label className='block text-xs font-semibold text-gray-600 uppercase mb-1'>Client ID</label>
                  <div className='flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2.5 font-mono text-xs'>
                    <span className='select-all text-gray-800'>{secretRevealModal.clientId}</span>
                    <button
                      onClick={() => handleCopy(secretRevealModal.clientId, 'modal-client-id')}
                      className='text-blue-600 hover:text-blue-700 text-xs font-medium ml-2'
                    >
                      {copiedKey === 'modal-client-id' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className='block text-xs font-semibold text-gray-600 uppercase mb-1'>
                  {secretRevealModal.isApiKey ? 'API Key' : 'Client Secret'}
                </label>
                <div className='flex items-center justify-between bg-gray-900 border border-gray-800 text-green-400 rounded-lg p-3 font-mono text-xs break-all'>
                  <span className='select-all'>{secretRevealModal.secret}</span>
                  <button
                    onClick={() => handleCopy(secretRevealModal.secret, 'modal-secret')}
                    className='bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded text-xs ml-3 flex-shrink-0'
                  >
                    {copiedKey === 'modal-secret' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <div className='mt-6 pt-4 border-t border-gray-100 flex justify-end'>
              <button
                onClick={() => setSecretRevealModal(null)}
                className='px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-lg text-sm font-medium transition-colors'
              >
                I have saved this secret safely
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OAuthManagementPage;
